import React, { useEffect, useState } from 'react';
import { Archive, ChevronDown, ChevronUp, Clock, Download, FileText, Forward, ListPlus, Mail, Reply, ReplyAll, ShieldAlert, ShieldCheck, Star, Tag, Trash2 } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Avatar, LabelPill } from './ui.jsx';

// Render a markdown/plaintext body to SANITIZED HTML. breaks:true keeps plain-text
// line breaks; DOMPurify strips any dangerous markup from (untrusted) mail.
function renderMarkdown(src) {
  try { return DOMPurify.sanitize(marked.parse(src || '', { gfm: true, breaks: true })); }
  catch { return DOMPurify.sanitize((src || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))); }
}

/** Reading pane: renders a whole conversation (thread) as stacked messages - the
 *  latest expanded, earlier ones collapsed. One action bar; reply/forward/labels
 *  act on the latest message. Falls back to a single message when not threaded. */
export function MessageView({ email, session, onArchive, onDelete, onJunk, onSnooze, onUnread, onStar, onReply, onForward, onTask, onLabel, allLabels = [] }) {
  const { jmap } = session;
  const [msgs, setMsgs] = useState(null);      // thread headers, oldest -> newest
  const [latestText, setLatestText] = useState(''); // decrypted latest body, for reply/forward
  const [labelOpen, setLabelOpen] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setMsgs(null); setLatestText(''); setErr(null); setLabelOpen(false);
    let cancelled = false;
    (async () => {
      try {
        let ids = [email.id];
        if (email.threadId) {
          const tr = await jmap.request([['Thread/get', { accountId: jmap.accountId, ids: [email.threadId] }, '0']]);
          const t = tr[0][1].list?.[0];
          if (t?.emailIds?.length) ids = t.emailIds;
        }
        const er = await jmap.request([['Email/get', { accountId: jmap.accountId, ids, properties: ['id', 'subject', 'from', 'to', 'receivedAt', 'preview', 'hasAttachment', 'keywords'] }, '0']]);
        if (cancelled) return;
        const list = (er[0][1].list || []).slice().sort((a, b) => new Date(a.receivedAt) - new Date(b.receivedAt));
        setMsgs(list.length ? list : [{ ...email, from: [{ name: email.fromName, email: email.fromEmail }] }]);
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [email.id, email.threadId, jmap]); // eslint-disable-line

  const subject = msgs?.[0]?.subject || email.subject || '(no subject)';
  const latestKw = (msgs && msgs[msgs.length - 1]?.keywords) || email.raw?.keywords || {};
  const myLabels = Object.keys(latestKw).filter((k) => k.startsWith('$km-label-')).map((k) => k.slice(10));

  const Act = ({ icon: I, label, onClick, color }) => <button className="km-btn-ghost" title={label} onClick={onClick} style={color ? { color } : undefined}><I size={16} />{label}</button>;
  return (
    <>
      <div className="km-actions">
        <Act icon={Archive} label="Archive" onClick={onArchive} /><Act icon={Trash2} label="Delete" onClick={onDelete} /><Act icon={Clock} label="Snooze" onClick={onSnooze} /><Act icon={Mail} label="Mark unread" onClick={onUnread} />
        <Act icon={Star} label={email.starred ? 'Starred' : 'Star'} onClick={onStar} color={email.starred ? 'var(--km-star)' : undefined} /><Act icon={ShieldAlert} label="Junk" onClick={onJunk} />
        {onLabel && (
          <div style={{ position: 'relative' }}>
            <button className="km-btn-ghost" title="Label" onClick={() => setLabelOpen((o) => !o)}><Tag size={16} />Label</button>
            {labelOpen && (
              <div className="km-card" style={{ position: 'absolute', zIndex: 20, top: '110%', left: 0, minWidth: 200, padding: 6, boxShadow: '0 8px 30px rgba(0,0,0,.18)' }} onMouseLeave={() => setLabelOpen(false)}>
                {allLabels.length === 0 && <div className="km-faint" style={{ fontSize: 12.5, padding: '6px 8px' }}>No labels yet. Create one in the sidebar.</div>}
                {allLabels.map((l) => { const on = myLabels.includes(l.toLowerCase()); return (
                  <button key={l} className="km-nav-item" style={{ width: '100%', padding: '6px 8px', fontSize: 13 }} onClick={() => onLabel(l, !on)}>
                    <span style={{ width: 15, display: 'inline-flex' }}>{on ? '✓' : ''}</span><span style={{ flex: 1, textAlign: 'left' }}>{l}</span>
                  </button>); })}
              </div>
            )}
          </div>
        )}
        <span className="km-faint" style={{ marginLeft: 'auto', fontSize: 12 }}>{fmtDate((msgs && msgs[msgs.length - 1]?.receivedAt) || email.receivedAt)}</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <article className="km-message">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0 }}>{subject}</h1>
            {myLabels.map((l) => <LabelPill key={l} name={l.replace(/^./, (c) => c.toUpperCase())} />)}
            {msgs && msgs.length > 1 && <span className="km-faint" style={{ fontSize: 12.5 }}>{msgs.length} messages</span>}
          </div>
          {err && <div style={{ color: 'var(--km-bad)', fontSize: 13 }}>{err}</div>}
          {!msgs && <div className="km-body-text"><span className="km-faint">Loading…</span></div>}
          {msgs && msgs.map((m, i) => (
            <MessageCard key={m.id} header={m} session={session} single={msgs.length === 1} defaultOpen={i === msgs.length - 1} onDecrypted={i === msgs.length - 1 ? setLatestText : undefined} />
          ))}
          {msgs && (
            <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
              <button className="km-btn-outline" onClick={() => onReply(false, latestText)}><Reply size={15} />Reply</button>
              <button className="km-btn-outline" onClick={() => onReply(true, latestText)}><ReplyAll size={15} />Reply all</button>
              <button className="km-btn-outline" onClick={() => onForward(latestText)}><Forward size={15} />Forward</button>
              <button className="km-btn-ghost" style={{ marginLeft: 'auto' }} onClick={onTask}><ListPlus size={15} />Add to tasks</button>
            </div>
          )}
        </article>
      </div>
    </>
  );
}

// One message in a conversation: header always visible; body fetched + decrypted
// when expanded (the latest message is open by default).
function MessageCard({ header, session, defaultOpen, single, onDecrypted }) {
  const { jmap, crypto } = session;
  const [open, setOpen] = useState(!!defaultOpen);
  const [full, setFull] = useState(null);
  const [text, setText] = useState('');
  const [sig, setSig] = useState(null);
  const [details, setDetails] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || full) return;
    let cancelled = false;
    (async () => {
      try {
        const msg = await jmap.getEmailBody(header.id); if (cancelled) return; setFull(msg);
        const raw = extractText(msg); const envs = tryParsePqc(raw);
        if (!envs) { setText(raw || header.preview || ''); setSig({ encrypted: false }); onDecrypted?.(raw || ''); return; }
        const me = (session.email || '').toLowerCase();
        const env = envs.find((e) => (e.to || '').toLowerCase() === me) || null;
        if (!env) { setSig({ encrypted: true, notForMe: true, suite: envs[0].suite, signer: envs[0].from }); setText(''); return; }
        const senderPub = await crypto.lookupRecipient(env.from);
        if (session.identity?.kem) {
          const out = await crypto.open({ envelope: env, recipientKemPrivateKey: session.identity.kem.privateKey, senderSigPublicKey: senderPub?.sigPublicKey });
          if (cancelled) return;
          setText(out.plaintext); setSig({ encrypted: true, valid: out.signature?.valid !== false, suite: env.suite, signer: env.from }); onDecrypted?.(out.plaintext);
        } else { setSig({ encrypted: true, pending: true, suite: env.suite, signer: env.from }); setText(''); }
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [open, header.id]); // eslint-disable-line

  const from = (full?.from?.[0]) || header.from?.[0] || { name: header.fromName, email: header.fromEmail };
  const to = (full?.to || header.to || []).map((t) => t.name || t.email || t).join(', ');
  const receipt = sig?.encrypted
    ? [['Suite', sig.suite || 'KM1-x25519.mlkem768-mldsa65-aes256gcm'], ['Key exchange', 'X25519 + ML-KEM-768'], ['Signature', `Ed25519 + ML-DSA-65 · ${sig.pending ? 'not yet verified' : sig.valid ? 'both valid' : 'INVALID'}`], ['Cipher', 'AES-256-GCM · HKDF-SHA256'], ['Signer', sig.signer], ['Transport', 'TLS 1.3 · X25519MLKEM768']]
    : [['Sender', from.email], ['Transport', 'TLS 1.3 · x25519 (classical fallback)'], ['At rest', 'Encrypted on server']];

  return (
    <div className="km-card" style={{ marginBottom: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: single ? 'default' : 'pointer' }} onClick={() => !single && setOpen((o) => !o)}>
        <Avatar name={from.name || from.email} size={32} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>{from.name || from.email} <span className="km-muted" style={{ fontWeight: 400 }}>{from.name ? from.email : ''}</span></div>
          {open ? <div className="km-faint" style={{ fontSize: 12.5 }}>to {to || 'me'}</div> : <div className="km-ellipsis km-faint" style={{ fontSize: 12.5 }}>{header.preview}</div>}
        </div>
        <span className="km-faint" style={{ fontSize: 12 }}>{fmtDate(header.receivedAt)}</span>
      </div>
      {open && (
        <div style={{ padding: '0 14px 12px' }}>
          {sig && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 10px' }}>
              {sig.encrypted && sig.valid !== false ? <ShieldCheck size={15} color="var(--km-ok)" /> : <ShieldAlert size={15} color={sig.encrypted ? 'var(--km-bad)' : 'var(--km-warn)'} />}
              <span style={{ flex: 1, fontSize: 12.5 }} className="km-faint">{sig.encrypted ? (sig.notForMe ? 'Encrypted - no copy on this message is addressed to you.' : sig.pending ? 'Encrypted. Unlock your key to read.' : sig.valid ? 'End-to-end encrypted · signature verified' : 'Encrypted, but the signature does NOT verify') : 'Not end-to-end encrypted. Delivered over TLS.'}</span>
              <button className="km-btn-ghost" style={{ padding: 0, fontSize: 12 }} onClick={(e) => { e.stopPropagation(); setDetails((d) => !d); }}>{details ? 'Hide' : 'Details'}{details ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</button>
            </div>
          )}
          {details && <div className="km-receipt" style={{ marginBottom: 8 }}>{receipt.map(([k, v]) => <React.Fragment key={k}><span className="km-faint">{k}</span><span className="km-mono">{v}</span></React.Fragment>)}</div>}
          {err && <div style={{ color: 'var(--km-bad)', fontSize: 13 }}>{err}</div>}
          <MessageBody text={text} loading={!full} />
          {full?.attachments?.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>{full.attachments.map((a) => <a key={a.blobId} className="km-attach" href={jmap.downloadUrl?.(a) || '#'} download={a.name}><FileText size={20} color="var(--km-muted)" /><span><b>{a.name}</b><small>{fmtSize(a.size)}</small></span><Download size={15} color="var(--km-faint)" /></a>)}</div>}
        </div>
      )}
    </div>
  );
}

// Body renderer: shows the author's own text, and collapses the quoted reply
// history (the "On … wrote:" + `>`-prefixed trailer) behind a toggle.
function MessageBody({ text, loading }) {
  const [showQuoted, setShowQuoted] = useState(false);
  if (loading) return <div className="km-body-text"><span className="km-faint">Loading…</span></div>;
  const { main, quoted } = splitQuoted(text || '');
  return (
    <div className="km-body-text km-md">
      {main ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(main) }} /> : (!quoted && <p className="km-faint">(no content)</p>)}
      {quoted && (
        <div style={{ marginTop: main ? 8 : 0 }}>
          <button className="km-btn-ghost" style={{ padding: '2px 10px', fontSize: 12.5, borderRadius: 999, border: '1px solid var(--km-line2)' }} onClick={() => setShowQuoted((v) => !v)}>{showQuoted ? 'Hide quoted text' : '··· Show quoted text'}</button>
          {showQuoted && <blockquote style={{ margin: '10px 0 0', paddingLeft: 14, borderLeft: '3px solid var(--km-line2)', color: 'var(--km-muted)' }}><div dangerouslySetInnerHTML={{ __html: renderMarkdown(dequote(quoted)) }} /></blockquote>}
        </div>
      )}
    </div>
  );
}

function splitQuoted(text) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let q = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*>/.test(lines[i])) { q = i; break; }
    if (i > 0 && /\bwrote:\s*$/.test(lines[i].trim()) && /^on\b/i.test(lines[i].trim())) { q = i; break; }
  }
  if (q === -1) return { main: text.trim(), quoted: '' };
  if (q > 0 && /\bwrote:\s*$/.test(lines[q - 1].trim())) q -= 1;
  return { main: lines.slice(0, q).join('\n').trim(), quoted: lines.slice(q).join('\n').trim() };
}

function dequote(q) { return q.split('\n').map((l) => l.replace(/^\s?>\s?/, '')).join('\n'); }

function extractText(msg) { if (!msg?.bodyValues) return ''; const part = msg.textBody?.[0] || msg.htmlBody?.[0]; return msg.bodyValues[part?.partId]?.value || ''; }
// Parse a PQC body into the ARRAY of envelopes it carries (one per recipient +
// sender). Returns null if the body is not a KryptaMail encrypted message.
function tryParsePqc(raw) {
  try {
    const s = raw.replace(/^\[KryptaMail post-quantum encrypted message\]\s*/, '');
    const o = JSON.parse(s);
    const arr = Array.isArray(o) ? o : [o];
    return arr.length && arr[0] && arr[0].suite && arr[0].kem && arr[0].body ? arr : null;
  } catch { return null; }
}
// True when a plain-text body is actually a KryptaMail encrypted envelope.
export function isPqcBody(s) { return typeof s === 'string' && s.startsWith('[KryptaMail post-quantum encrypted message]'); }
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const fmtSize = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1e3)} KB`);
